import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Tab,
  Tabs,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Paper,
  InputAdornment,
  Autocomplete,
} from "@mui/material";
import { useQuery, useMutation } from "@apollo/client";
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  CheckCircle as CheckCircleIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Circle as CircleIcon,
} from "@mui/icons-material";
import {
  GET_INBOX,
  GET_SENT_NOTIFICATIONS,
  GET_NOTIFICATION_OUTBOX,
  GET_INSTRUCTOR_LIST,
  GET_CLASS_LIST,
  GET_STUDENT_LIST,
  ME,
} from "../graphql/query";
import {
  SEND_NOTIFICATION,
  MARK_NOTIFICATION_AS_READ,
} from "../graphql/mutation";
import themestyle from "../theme";
import CustomSnackbar from "../components/Snackbar";
import { NOTIFICATION_PAGE_TEXT } from "../utils/const";
import { stripNotificationReference } from "../utils/notification";

const NotificationManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [tabIndex, setTabIndex] = useState(0);

  useEffect(() => {
    document.title = NOTIFICATION_PAGE_TEXT.PAGE_TITLE;
  }, []);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState<
    "success" | "error" | "info"
  >("success");

  const [searchQuery, setSearchQuery] = useState("");

  // User Auth
  const { data: userData } = useQuery(ME, {
    fetchPolicy: "cache-first",
  });
  const user = userData?.me || JSON.parse(localStorage.getItem("user") || "{}");
  const userRole = user?.role?.toUpperCase() || "";
  const userId = user?.id || user?._id || "";

  const isSuperAdmin = userRole === "SUPER_ADMIN";
  const broadcastTabIndex = isSuperAdmin ? 3 : 2;


  // Queries
  const {
    data: inboxData,
    loading: inboxLoading,
    refetch: refetchInbox,
  } = useQuery(GET_INBOX, { fetchPolicy: "cache-and-network" });

  const {
    data: sentData,
    loading: sentLoading,
    refetch: refetchSent,
  } = useQuery(GET_SENT_NOTIFICATIONS, { fetchPolicy: "cache-and-network" });
  const {
    data: outboxData,
    loading: outboxLoading,
    refetch: refetchOutbox,
  } = useQuery(GET_NOTIFICATION_OUTBOX, {
    fetchPolicy: "cache-and-network",
    skip: !isSuperAdmin,
  });

  const { data: instructorsData } = useQuery(GET_INSTRUCTOR_LIST, {
    variables: { page: 1, limit: 1000 },
    skip: userRole !== "ADMIN" && userRole !== "SUPER_ADMIN",
  });

  const { data: studentsData } = useQuery(GET_STUDENT_LIST, {
    variables: { page: 1, perPage: 1000 },
    skip: userRole !== "ADMIN" && userRole !== "SUPER_ADMIN",
  });

  // Always fetch all classes; we filter client-side for INSTRUCTOR role below
  const { data: classData } = useQuery(GET_CLASS_LIST, {
    variables: { page: 1, perPage: 1000 },
    fetchPolicy: "network-only",
  });


  // Mutations
  const [sendNotification, { loading: sending }] =
    useMutation(SEND_NOTIFICATION);
  const [markAsRead] = useMutation(MARK_NOTIFICATION_AS_READ);

  // Form State
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [targetAudience, setTargetAudience] = useState("");
  const [targetInstructorId, setTargetInstructorId] = useState("");
  const [targetClassId, setTargetClassId] = useState("");
  const [targetStudentId, setTargetStudentId] = useState("");

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
    if (newValue === 0) refetchInbox();
    if (newValue === 1) refetchSent();
    if (isSuperAdmin && newValue === 2) refetchOutbox();
  };

  const handleMarkAsRead = async (id: string, isRead: boolean) => {
    if (isRead) return;
    try {
      await markAsRead({ variables: { id } });
      refetchInbox();
    } catch (error) {
      console.error(error);
    }
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      let targetClassName = "";
      if (targetAudience === "CLASS_STUDENTS" && targetClassId) {
        const cList = classData?.getClassList?.data || [];
        const found = cList.find((c: any) => c.id === targetClassId);
        if (found) targetClassName = found.className;
      }

      const input: Record<string, any> = {
        title,
        message,
        targetAudience,
        targetInstructorId: targetInstructorId || null,
        targetClassId: targetClassId || null,
        targetClassName: targetClassName || null,
      };
      if (targetStudentId) {
        input.targetUserId = targetStudentId;
      }

      const { data } = await sendNotification({ variables: { input } });
      if (data?.sendNotification?.success) {
        setSnackbarMessage("Notification sent successfully!");
        setSnackbarType("success");
        setSnackbarOpen(true);
        setTitle("");
        setMessage("");
        setTargetAudience("");
        setTargetInstructorId("");
        setTargetClassId("");
        setTargetStudentId("");
        setTabIndex(1); // switch to sent
        refetchSent();
      } else {
        throw new Error(data?.sendNotification?.message || "Failed to send");
      }
    } catch (error: any) {
      setSnackbarMessage(error.message || "An error occurred");
      setSnackbarType("error");
      setSnackbarOpen(true);
    }
  };

  // Render variables
  const inboxList = inboxData?.getInbox?.data || [];
  const sentList = sentData?.getSentNotifications?.data || [];
  const outboxList = outboxData?.getNotificationOutbox?.data || [];
  const instructors = instructorsData?.getInstructorList?.data || [];
  const students = studentsData?.getStudentList?.data || [];

  // For INSTRUCTOR: only show their own classes (match by instructorName)
  // For ADMIN/SUPER_ADMIN: show all classes
  const allClasses: any[] = classData?.getClassList?.data || [];
  const myClasses =
    userRole === "INSTRUCTOR"
      ? allClasses.filter(
        (c: any) =>
          c.instructorName?.trim().toLowerCase() ===
          user?.fullname?.trim().toLowerCase(),
      )
      : allClasses;

  const filteredInbox = inboxList.filter((notif: any) =>
    (notif.title + notif.message)
      .toLowerCase()
      .includes(searchQuery.toLowerCase()),
  );

  const filteredSent = sentList.filter((notif: any) =>
    (notif.title + notif.message)
      .toLowerCase()
      .includes(searchQuery.toLowerCase()),
  );

  const filteredOutbox = outboxList.filter((notif: any) =>
    `${notif.senderName} ${notif.senderRole} ${notif.title} ${notif.message} ${notif.targetAudience}`
      .toLowerCase()
      .includes(searchQuery.toLowerCase()),
  );


  const timeAgo = (date: Date) => {
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
    const intervals = {
      year: 31536000,
      month: 2592000,
      week: 604800,
      day: 86400,
      hour: 3600,
      minute: 60,
    };

    for (const [key, value] of Object.entries(intervals)) {
      const interval = Math.floor(seconds / value);
      if (interval >= 1) {
        return interval === 1 ? `1 ${key} ago` : `${interval} ${key}s ago`;
      }
    }
    return "just now";
  };

  const formatTime = (dateParam: string | number) => {
    const d = new Date(Number(dateParam) || dateParam);
    const diffHours = (new Date().getTime() - d.getTime()) / (1000 * 60 * 60);
    // If less than 24 hours, show timeAgo, else show Today/Yesterday or Date
    if (diffHours < 24) {
      return timeAgo(d);
    } else {
      // A realistic mock date formatter like "Today at 16:00" or "Yesterday at 10:15"
      const today = new Date();
      const isYesterday = diffHours >= 24 && diffHours < 48;
      const timeStr = d.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
      if (isYesterday) return `Yesterday at ${timeStr}`;
      return `${d.toLocaleDateString()} at ${timeStr}`;
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        backgroundColor: "#F7FAF3",
        borderRadius: 3,
        p: { xs: 2, sm: 3 },
        minHeight: "100%",
      }}
    >
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          fontSize: "24px",
          color: "#111827",
          mb: 1,
          pl: 1,
          mt: 2,
        }}
      >
        Notifications Center
      </Typography>

      <Box sx={{ width: "100%", maxWidth: "800px", pl: 1, pr: 1 }}>
        <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
          <TextField
            placeholder="Search"
            variant="outlined"
            size="small"
            fullWidth
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#9CA3AF" }} />
                </InputAdornment>
              ),
              sx: {
                borderRadius: "8px",
                backgroundColor: "#FFFFFF",
                "& fieldset": {
                  borderColor: "#E5E7EB",
                },
              },
            }}
          />
        </Box>
        <Box sx={{ borderBottom: 1, borderColor: "#E5E7EB", mb: 3 }}>
          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            aria-label="notification tabs"
            TabIndicatorProps={{
              style: {
                backgroundColor: themestyle.colors.activeGreen,
                height: 3,
                display: userRole === "USER" ? "none" : "block",
              },
            }}
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 500,
                color: "#6B7280",
                minWidth: "auto",
                px: { xs: 2, sm: 4 },
                fontSize: "15px",
              },
              "& .Mui-selected": {
                color: `${themestyle.colors.activeGreen} !important`,
              },
            }}
          >
            <Tab label="Inbox" />
            {userRole !== "USER" && userRole !== "INSTRUCTOR" && <Tab label="Sent" />}
            {isSuperAdmin && <Tab label="Outbox" />}
            {userRole !== "USER" && userRole !== "INSTRUCTOR" && <Tab label="Broadcast" />}
          </Tabs>
        </Box>

        {/* INBOX TAB */}
        {tabIndex === 0 && (
          <Box>
            {inboxLoading ? (
              <CircularProgress />
            ) : filteredInbox.length === 0 ? (
              <Typography sx={{ color: "#6B7280" }}>
                No notifications in your inbox.
              </Typography>
            ) : (
              filteredInbox.map((notif: any) => (
                <Paper
                  key={notif.id}
                  elevation={0}
                  sx={{
                    p: 2.5,
                    mb: 2,
                    backgroundColor: "#FFFFFF",
                    border: "1px solid #E5E7EB",
                    borderRadius: "12px",
                    cursor: notif.isRead ? "default" : "pointer",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      boxShadow: notif.isRead
                        ? "none"
                        : "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                    },
                  }}
                  onClick={() => handleMarkAsRead(notif.id, notif.isRead)}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <Box>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          color: "#111827",
                          mb: 0.5,
                          fontSize: "16px",
                        }}
                      >
                        {notif.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "#4B5563", mb: 1, fontSize: "14px" }}
                      >
                        {stripNotificationReference(notif.message)}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "#9CA3AF", fontSize: "13px" }}
                      >
                        {formatTime(notif.created_at)}
                      </Typography>
                    </Box>
                    <Box>
                      {!notif.isRead ? (
                        notif.title?.toLowerCase().includes("successful") || notif.title?.toLowerCase().includes("success") ? (
                          <CheckCircleIcon sx={{ color: "#4ADE80", fontSize: 20, mt: 0.5 }} />
                        ) : (
                          <CircleIcon sx={{ color: "#4ADE80", fontSize: 16, mt: 0.5 }} />
                        )
                      ) : (
                        <CheckCircleOutlineIcon
                          sx={{ color: "#9CA3AF", fontSize: 20, mt: 0.5 }}
                        />
                      )}
                    </Box>
                  </Box>
                </Paper>
              ))
            )}
          </Box>
        )}

        {/* SENT TAB */}
        {tabIndex === 1 && (
          <Box>
            {sentLoading ? (
              <CircularProgress />
            ) : filteredSent.length === 0 ? (
              <Typography sx={{ color: "#6B7280" }}>
                You haven't sent any notifications yet.
              </Typography>
            ) : (
              filteredSent.map((notif: any) => {
                const toLabel = (() => {
                  switch (notif.targetAudience) {
                    case "ALL_INSTRUCTORS":
                      return "All Instructors";
                    case "ALL_STUDENTS":
                      return "All Students";
                    case "SPECIFIC_INSTRUCTOR": {
                      const inst = instructors.find(
                        (i: any) =>
                          (i.instructorId || i.id) === notif.targetInstructorId,
                      );
                      return inst ? inst.fullname : "Specific Instructor";
                    }
                    case "CLASS_STUDENTS":
                      return notif.targetClassName
                        ? `${notif.targetClassName} (Students)`
                        : "Class Students";
                    case "SPECIFIC_STUDENT": {
                      const stu = students.find(
                        (s: any) => (s.studentId || s.id) === notif.targetUserId,
                      );
                      return stu ? stu.fullName : "Specific Student";
                    }
                    default:
                      return notif.targetAudience;
                  }
                })();

                return (
                  <Paper
                    key={notif.id}
                    elevation={0}
                    sx={{
                      p: 2.5,
                      mb: 2,
                      backgroundColor: "#FFFFFF",
                      border: "1px solid #E5E7EB",
                      borderRadius: "12px",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            backgroundColor: "#F0FDF4",
                            border: "1px solid #BBF7D0",
                            borderRadius: "6px",
                            px: 1,
                            py: 0.3,
                            mb: 1,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "12px",
                              fontWeight: 500,
                              color: "#16A34A",
                            }}
                          >
                            To: {toLabel}
                          </Typography>
                        </Box>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            color: "#111827",
                            mb: 0.5,
                            fontSize: "16px",
                          }}
                        >
                          {notif.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "#4B5563", mb: 1, fontSize: "14px" }}
                        >
                          {stripNotificationReference(notif.message)}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "#9CA3AF", fontSize: "13px" }}
                        >
                          {formatTime(notif.created_at)}
                        </Typography>
                      </Box>
                      <Box>
                        <CheckCircleIcon
                          sx={{ color: "#9CA3AF", fontSize: 20, mt: 0.5 }}
                        />
                      </Box>
                    </Box>
                  </Paper>
                );
              })
            )}
          </Box>
        )}

        {/* OUTBOX TAB */}
        {isSuperAdmin && tabIndex === 2 && (
          <Box>
            {outboxLoading ? (
              <CircularProgress />
            ) : filteredOutbox.length === 0 ? (
              <Typography sx={{ color: "#6B7280" }}>
                No outgoing notifications found.
              </Typography>
            ) : (
              filteredOutbox.map((notif: any) => {
                const toLabel = (() => {
                  switch (notif.targetAudience) {
                    case "ALL_INSTRUCTORS":
                      return "All Instructors";
                    case "ALL_STUDENTS":
                      return "All Students";
                    case "SPECIFIC_INSTRUCTOR": {
                      const inst = instructors.find(
                        (i: any) =>
                          (i.instructorId || i.id) === notif.targetInstructorId,
                      );
                      return inst ? inst.fullname : "Specific Instructor";
                    }
                    case "CLASS_STUDENTS":
                      return notif.targetClassName
                        ? `${notif.targetClassName} (Students)`
                        : "Class Students";
                    case "SPECIFIC_STUDENT": {
                      const stu = students.find(
                        (s: any) => (s.studentId || s.id) === notif.targetUserId,
                      );
                      return stu ? stu.fullName : "Specific Student";
                    }
                    case "ADMIN_INBOX":
                      return "Admin Inbox";
                    case "SUPER_ADMIN_ONLY":
                      return "Super Admin Only";
                    default:
                      return notif.targetAudience;
                  }
                })();

                return (
                  <Paper
                    key={notif.id}
                    elevation={0}
                    sx={{
                      p: 2.5,
                      mb: 2,
                      backgroundColor: "#FFFFFF",
                      border: "1px solid #E5E7EB",
                      borderRadius: "12px",
                    }}
                  >
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: 600,
                          color: "#16A34A",
                        }}
                      >
                        {/* From: {notif.senderName} ({notif.senderRole}) */}
                        From: {notif.senderEmail || notif.senderName}

                      </Typography>

                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: 600,
                          color: "#2563EB",
                        }}
                      >
                        {/* To: {toLabel} */}
                        To: {notif.receiverEmail || toLabel}

                      </Typography>
                    </Box>

                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        color: "#111827",
                        mb: 0.5,
                        fontSize: "16px",
                      }}
                    >
                      {notif.title}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ color: "#4B5563", mb: 1, fontSize: "14px" }}
                    >
                      {stripNotificationReference(notif.message)}
                    </Typography>

                    <Typography
                      variant="caption"
                      sx={{ color: "#9CA3AF", fontSize: "13px" }}
                    >
                      {formatTime(notif.created_at)}
                    </Typography>
                  </Paper>
                );
              })
            )}
          </Box>
        )}

        {/* BROADCAST TAB */}
        {tabIndex === broadcastTabIndex && (
          <Box
            component="form"
            onSubmit={handleSend}
            sx={{ width: "100%", pt: 1 }}
          >
            <Typography
              variant="subtitle1"
              fontWeight={700}
              sx={{ color: "#111827", mb: 2, fontSize: "16px" }}
            >
              Broadcast Notification
            </Typography>

            <TextField
              fullWidth
              required
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              sx={{
                mb: 3,
                backgroundColor: "#FFFFFF",
                "& fieldset": { borderColor: "#E5E7EB" },
              }}
            />

            <TextField
              fullWidth
              required
              multiline
              rows={4}
              label="Message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              sx={{
                mb: 3,
                backgroundColor: "#FFFFFF",
                "& fieldset": { borderColor: "#E5E7EB" },
              }}
            />

            {(userRole === "ADMIN" || userRole === "SUPER_ADMIN") && (
              <>
                <TextField
                  select
                  fullWidth
                  required
                  label="Target Audience"
                  value={targetAudience}
                  onChange={(e) => {
                    setTargetAudience(e.target.value);
                    setTargetInstructorId("");
                    setTargetClassId("");
                    setTargetStudentId("");
                  }}
                  sx={{
                    mb: 3,
                    backgroundColor: "#FFFFFF",
                    "& fieldset": { borderColor: "#E5E7EB" },
                  }}
                >
                  <MenuItem value="ALL_INSTRUCTORS">All Instructors</MenuItem>
                  <MenuItem value="SPECIFIC_INSTRUCTOR">Specific Instructor</MenuItem>
                  <MenuItem value="ALL_STUDENTS">All Students</MenuItem>
                  <MenuItem value="SPECIFIC_STUDENT">Specific Student</MenuItem>
                </TextField>

                {targetAudience === "SPECIFIC_INSTRUCTOR" && (
                  <Autocomplete
                    fullWidth
                    options={instructors}
                    getOptionLabel={(option: any) => option.fullname || ""}
                    value={instructors.find((inst: any) => (inst.instructorId || inst.id) === targetInstructorId) || null}
                    onChange={(_event, newValue: any) => {
                      setTargetInstructorId(newValue ? (newValue.instructorId || newValue.id) : "");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        label="Select Instructor"
                        sx={{
                          mb: 3,
                          backgroundColor: "#FFFFFF",
                          "& fieldset": { borderColor: "#E5E7EB" },
                        }}
                      />
                    )}
                  />
                )}

                {targetAudience === "SPECIFIC_STUDENT" && (
                  <Autocomplete
                    fullWidth
                    options={students}
                    getOptionLabel={(option: any) => option.fullName || ""}
                    value={students.find((s: any) => (s.studentId || s.id) === targetStudentId) || null}
                    onChange={(_event, newValue: any) => {
                      setTargetStudentId(newValue ? (newValue.studentId || newValue.id) : "");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        label="Select Student"
                        sx={{
                          mb: 3,
                          backgroundColor: "#FFFFFF",
                          "& fieldset": { borderColor: "#E5E7EB" },
                        }}
                      />
                    )}
                  />
                )}
              </>
            )}

            {userRole === "INSTRUCTOR" && (
              <>
                <TextField
                  select
                  fullWidth
                  required
                  label="Target Audience"
                  value={targetAudience}
                  onChange={(e) => {
                    setTargetAudience(e.target.value);
                    setTargetClassId("");
                  }}
                  sx={{
                    mb: 3,
                    backgroundColor: "#FFFFFF",
                    "& fieldset": { borderColor: "#E5E7EB" },
                  }}
                >
                  <MenuItem value="CLASS_STUDENTS">
                    Students in a specific Class
                  </MenuItem>
                </TextField>

                {targetAudience === "CLASS_STUDENTS" && (
                  <TextField
                    select
                    fullWidth
                    required
                    label="Select Class"
                    value={targetClassId}
                    onChange={(e) => setTargetClassId(e.target.value)}
                    sx={{
                      mb: 3,
                      backgroundColor: "#FFFFFF",
                      "& fieldset": { borderColor: "#E5E7EB" },
                    }}
                  >
                    {myClasses.length === 0 && (
                      <MenuItem disabled value="">
                        {classData ? "No classes assigned" : "Loading..."}
                      </MenuItem>
                    )}
                    {myClasses.map((cls: any) => (
                      <MenuItem key={cls.id} value={cls.id}>
                        {cls.className}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </>
            )}

            <Button
              type="submit"
              variant="contained"
              disabled={
                sending ||
                !targetAudience ||
                (targetAudience === "SPECIFIC_INSTRUCTOR" && !targetInstructorId) ||
                (targetAudience === "CLASS_STUDENTS" && !targetClassId) ||
                (targetAudience === "SPECIFIC_STUDENT" && !targetStudentId)
              }
              sx={{
                mt: 1,
                fontSize: "14px",
                fontWeight: 600,
                letterSpacing: "0.5px",
                px: 4,
                py: 1.2,
                borderRadius: "8px",
                backgroundColor: themestyle.colors.activeGreen,
                "&:hover": { backgroundColor: "#65A30D" },
                "&.Mui-disabled": {
                  backgroundColor: "#E5E7EB",
                  color: "#9CA3AF",
                },
              }}
            >
              {sending ? "SENDING..." : "SEND NOTIFICATION"}
            </Button>
          </Box>
        )}
      </Box>

      <CustomSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        type={snackbarType}
        onClose={() => setSnackbarOpen(false)}
      />
    </Box>
  );
};

export default NotificationManagement;
